'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { FeatureFlagsService } from '../services/feature-flags.service';
import strapiFeatureFlags from '../../../repositories/aecomStrapiCms/featureFlags';
import { TFeatureFlag } from '../../../repositories/aecomStrapiCms/featureFlags/types';

interface FeatureFlagsContextType {
  isEnabled: (key: string) => boolean;
  getAllFlags: () => Record<string, TFeatureFlag>;
  isLoading: boolean;
  isInitialized: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined,
);

interface FeatureFlagsProviderProps {
  children: React.ReactNode;
}

const client = strapiFeatureFlags({
  baseUrl: process.env.BFF_API_BASE_URL!,
});

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
}) => {
  const [service] = useState(() => new FeatureFlagsService());
  const [isInitialized, setIsInitialized] = useState(false);
  const { data: featureFlags, isLoading } = client.useFeatureFlags();

  useEffect(() => {
    if (featureFlags) {
      service.initialize(featureFlags);
      setIsInitialized(true);
    }
  }, [featureFlags, service]);

  const contextValue = React.useMemo(
    () => ({
      isEnabled: (key: string) => {
        if (!isInitialized) {
          return false;
        }
        return service.isEnabled(key);
      },
      getAllFlags: () => service.getAllFlags(),
      isLoading,
      isInitialized,
    }),
    [service, isLoading, isInitialized],
  );

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider',
    );
  }
  return context;
};
